import type { EmployeeSales, ProductSales, SalesSummary } from "../reports/sales-report";
import { formatMoney } from "../utils/format";
import { buildDataset, type ExportColumn, type ExportDataset, type ExportMetadata } from "./types";

/**
 * Comandero adapters (K1): map the sales report aggregates into the generic
 * {@link ExportDataset}. Domain knowledge stops here — the CSV/PDF generators
 * never learn about orders or payments.
 *
 * Money is stored in minor units (cents); it is rendered with `formatMoney`
 * so the exported figures match the dashboard exactly. The report summary
 * (tickets / gross / average / tips / voids) travels as document metadata:
 * the PDF/HTML header renders it; the CSV carries the tabular breakdown.
 */

/** Summary KPIs as metadata key/values (rendered in the PDF/HTML header). */
export function salesSummaryExtra(summary: SalesSummary): Record<string, string> {
  return {
    Tickets: String(summary.ticketCount),
    "Gross sales": formatMoney(summary.grossSales),
    "Average ticket": formatMoney(summary.averageTicket),
    Tips: formatMoney(summary.tips),
    "Voided orders": String(summary.voidCount),
  };
}

export const PRODUCT_SALES_EXPORT_COLUMNS: ExportColumn<ProductSales>[] = [
  { key: "product", header: "Product", accessor: (r) => r.name },
  { key: "qty", header: "Qty", accessor: (r) => r.qty },
  { key: "total", header: "Total", accessor: (r) => formatMoney(r.total) },
];

/**
 * Builds the sales-report dataset: per-product breakdown as rows + the
 * summary KPIs as metadata. Ready for `exportDatasetAsCsv`/`exportDatasetAsPdf`.
 */
export function buildSalesReportDataset(
  summary: SalesSummary,
  byProduct: readonly ProductSales[],
  metadata: ExportMetadata = {},
): ExportDataset {
  return buildDataset(PRODUCT_SALES_EXPORT_COLUMNS, byProduct, {
    title: "Sales report",
    ...metadata,
    extra: { ...salesSummaryExtra(summary), ...metadata.extra },
  });
}

/**
 * Per-employee breakdown (who charged what, with tips). The caller resolves
 * employee ids to display names (same map the dashboard already builds).
 */
export function employeeSalesExportColumns(
  employeeName: (id: string) => string,
): ExportColumn<EmployeeSales>[] {
  return [
    { key: "employee", header: "Employee", accessor: (r) => employeeName(r.employeeId) },
    { key: "orders", header: "Orders", accessor: (r) => r.orders },
    { key: "total", header: "Total", accessor: (r) => formatMoney(r.total) },
    { key: "tips", header: "Tips", accessor: (r) => formatMoney(r.tips) },
  ];
}

/** Builds the per-employee dataset (summary KPIs in the metadata too). */
export function buildEmployeeSalesDataset(
  summary: SalesSummary,
  byEmployee: readonly EmployeeSales[],
  employeeName: (id: string) => string,
  metadata: ExportMetadata = {},
): ExportDataset {
  return buildDataset(employeeSalesExportColumns(employeeName), byEmployee, {
    title: "Sales by employee",
    ...metadata,
    extra: { ...salesSummaryExtra(summary), ...metadata.extra },
  });
}
